import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PermissionsPanel({ users, loading, onChanged }) {
  const { user: me, refresh } = useAuth();
  const [modules, setModules] = useState([]);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/permissions`).then(({ data }) => setModules(data)).catch(() => setModules([]));
  }, []);

  const toggle = useCallback(async (u, key, checked) => {
    const next = checked ? [...new Set([...(u.permissions || []), key])] : (u.permissions || []).filter((p) => p !== key);
    setSaving(`${u.id}:${key}`);
    try {
      await axios.put(`${API}/admin/users/${u.id}/permissions`, { permissions: next });
      toast.success(`${checked ? "Acceso concedido" : "Acceso retirado"}: ${u.email}`);
      if (u.id === me?.id) await refresh();
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar el permiso");
    } finally {
      setSaving(null);
    }
  }, [me, refresh, onChanged]);

  return (
    <Card className="shadow-none border-border" data-testid="permissions-panel">
      <CardHeader className="border-b border-border pb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          <h2 className="font-heading text-base font-bold tracking-tight">Permisos de acceso por módulo</h2>
        </div>
        <p className="text-xs text-muted-foreground">
          Activa o desactiva el acceso de cada usuario a los módulos restringidos. El cambio es inmediato (no hace falta volver a entrar).
          El administrador principal siempre tiene todos los permisos.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && users.length === 0 ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <table className="w-full text-sm" data-testid="permissions-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground text-left">
                <th className="py-2 pr-3 font-medium">Usuario</th>
                <th className="py-2 pr-3 font-medium">Rol</th>
                {modules.map((m) => (
                  <th key={m.key} className="py-2 pr-3 font-medium" title={m.description}>{m.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} data-testid={`perm-row-${u.email}`}>
                  <td className="py-2.5 pr-3">
                    <p className="font-semibold leading-tight">{u.name || u.email.split("@")[0]}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </td>
                  <td className="py-2.5 pr-3">
                    <Badge className={`rounded-md ${u.role === "admin" ? "bg-violet-100 text-violet-800 hover:bg-violet-100" : "bg-blue-100 text-blue-800 hover:bg-blue-100"}`}>
                      {u.role}
                    </Badge>
                  </td>
                  {modules.map((m) => {
                    const on = (u.permissions || []).includes(m.key);
                    return (
                      <td key={m.key} className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            data-testid={`perm-switch-${m.key}-${u.email}`}
                            checked={on}
                            disabled={u.protected || saving === `${u.id}:${m.key}`}
                            onCheckedChange={(v) => toggle(u, m.key, v)}
                            aria-label={`${m.label} — ${u.email}`}
                          />
                          {u.protected ? (
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> siempre</span>
                          ) : (
                            <span className={`text-xs ${on ? "text-emerald-700" : "text-muted-foreground"}`} data-testid={`perm-state-${m.key}-${u.email}`}>
                              {on ? "con acceso" : "sin acceso"}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
